import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraSurface } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { copyToClipboard } from '../../utils/downloadUtils';
import { toast } from '../toast';
/** The follow-ups offered on text selected in an answer. */ export const SELECTION_ACTIONS = [
    {
        id: 'check',
        label: 'Check this',
        prompt: (q)=>`Check this against your sources and tell me exactly where it comes from. If you can't trace it, say so:\n\n> ${q}`
    },
    {
        id: 'explain',
        label: 'Explain',
        prompt: (q)=>`Explain this in plain words:\n\n> ${q}`
    },
    {
        id: 'rewrite',
        label: 'Rewrite',
        prompt: (q)=>`Rewrite this to be clearer and shorter, keeping every fact:\n\n> ${q}`
    }
];
/** The selection as one line (Copy uses it whole). */ export function flattenSelection(text) {
    return text.replace(/\s+/g, ' ').trim();
}
/** Quote the selection as one paragraph of a markdown blockquote, capped for a prompt. */ export function quoteSelection(text) {
    return flattenSelection(text).slice(0, 1200);
}
/**
 * Select text in an answer to act on just that part: check it against the
 * sources, explain it, rewrite it, or copy it. The first three send a
 * follow-up into this chat.
 */ export function SelectionActions({ container, send }) {
    const [spot, setSpot] = useState(null);
    const read = useCallback(()=>{
        const sel = typeof window === 'undefined' ? null : window.getSelection();
        const root = container.current;
        if (!sel || sel.isCollapsed || !root || sel.rangeCount === 0) return setSpot(null);
        const range = sel.getRangeAt(0);
        if (!root.contains(range.commonAncestorContainer)) return setSpot(null);
        const text = flattenSelection(sel.toString());
        if (text.length < 3) return setSpot(null);
        // Some environments (tests) have no Range geometry: anchor to the answer.
        const rect = typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : root.getBoundingClientRect();
        setSpot({
            text,
            top: Math.max(8, rect.top - 44),
            left: rect.left + rect.width / 2
        });
    }, [
        container
    ]);
    useEffect(()=>{
        const root = container.current;
        if (!root) return;
        const onUp = ()=>setTimeout(read, 0);
        const onDown = (e)=>{
            if (!e.target?.closest?.('[data-selection-menu]')) setSpot(null);
        };
        const onKey = (e)=>{
            if (e.key === 'Escape') setSpot(null);
            else if (e.shiftKey) setTimeout(read, 0);
        };
        root.addEventListener('mouseup', onUp);
        root.addEventListener('keyup', onKey);
        document.addEventListener('mousedown', onDown);
        window.addEventListener('scroll', onDown, true);
        return ()=>{
            root.removeEventListener('mouseup', onUp);
            root.removeEventListener('keyup', onKey);
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('scroll', onDown, true);
        };
    }, [
        container,
        read
    ]);
    if (!spot || typeof document === 'undefined') return null;
    const act = (prompt)=>{
        send?.(prompt);
        window.getSelection()?.removeAllRanges();
        setSpot(null);
    };
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs(SpecraSurface, {
        className: "fixed z-50 flex -translate-x-1/2 items-center gap-0.5 border p-0.5 shadow-md",
        "data-selection-menu": "",
        "data-testid": Testids.assistant.selectionMenu,
        radius: "md",
        role: "toolbar",
        "aria-label": "Act on the selected text",
        style: {
            top: spot.top,
            left: spot.left
        },
        tone: "card",
        children: [
            send && SELECTION_ACTIONS.map((a)=>/*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.assistant.selectionAction,
                    onClick: ()=>act(a.prompt(quoteSelection(spot.text))),
                    size: "sm",
                    variant: "ghost",
                    children: a.label
                }, a.id)),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.assistant.selectionAction,
                onClick: ()=>{
                    void copyToClipboard(spot.text).then((ok)=>ok ? toast({
                            type: 'success',
                            title: 'Copied'
                        }) : toast({
                            type: 'error',
                            title: 'Could not copy. Select the text and copy it yourself.'
                        }));
                    setSpot(null);
                },
                size: "sm",
                variant: "ghost",
                children: "Copy"
            })
        ]
    }), document.body);
}
